import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';

import { Button, CompletionButton, Icon, IconButton, Stack, TaskTile, Text } from 'shell/components';
import { getTooltipComponent, getUserPreferredFullName } from 'utils/helper';

const SubtaskSection = ({
    disabled,
    isTaskDrawerOpen,
    subtasks = [],
    updateSubtaskStatus,
    deleteSubtask,
    getUserByAccountId,
    onAddSubtask,
    onOpenSubtask,
}) => {
    const [isEditing, setIsEditing] = useState(false);

    const renderTitle = () => {
        if (!subtasks.length) return 'Subtasks (0)';
        const numCompleted = subtasks.filter((subtask) => subtask.status === 'completed').length;
        return `Subtasks (${numCompleted}/${subtasks.length})`;
    };

    const handleDeleteSubtask = (e, id) => {
        e && e.stopPropagation();
        deleteSubtask(id);
    };

    const toggleComplete = (e, id, status) => {
        e && e.stopPropagation();
        if (status !== 'completed') {
            updateSubtaskStatus(id, 'completed');
        } else {
            updateSubtaskStatus(id, 'toDo');
        }
    };

    useEffect(() => {
        if (!isTaskDrawerOpen) {
            setTimeout(() => {
                setIsEditing(false);
            }, 100);
        }
    }, [isTaskDrawerOpen]);

    return (
        <CustomStack alignItems={'flex-start'} disabled={disabled} spacing={1}>
            <Stack sx={{ width: '100%' }} direction={'row'} justifyContent={'space-between'}>
                <Text
                    aria-disabled={disabled}
                    tooltip={{
                        component: getTooltipComponent('Subtasks', 'Use subtasks to break work into smaller parts'),
                    }}
                    variant={'p2'}
                    weight={'semibold'}
                >
                    {renderTitle()}
                </Text>
                {subtasks.length ? (
                    <Button
                        color={'neutral'}
                        disabled={disabled}
                        size={'small'}
                        variant={'borderless'}
                        onClick={() => setIsEditing((current) => !current)}
                    >
                        {isEditing ? 'Done' : 'Edit'}
                    </Button>
                ) : null}
            </Stack>
            {subtasks.map((subtask) => {
                return (
                    <TaskTile
                        data-cy={subtask.title}
                        key={subtask.id}
                        endComponent={
                            isEditing ? (
                                <IconButton
                                    aria-label={'delete'}
                                    kind={'exit'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={(e) => handleDeleteSubtask(e, subtask.id)}
                                />
                            ) : (
                                <CompletionButton
                                    aria-label={subtask.status === 'completed' ? 'uncomplete' : 'complete'}
                                    checked={subtask.status === 'completed'}
                                    onClick={(e) => toggleComplete(e, subtask.id, subtask.status)}
                                />
                            )
                        }
                        getNameById={(id) => getUserPreferredFullName(getUserByAccountId(id))}
                        task={subtask}
                        onClick={() => onOpenSubtask(subtask.id)}
                    />
                );
            })}
            <Button
                data-cy={'add-subtask-button'}
                disabled={disabled}
                startIcon={<Icon kind={'add'} />}
                variant={'tertiary'}
                onClick={onAddSubtask}
            >
                Add Subtask
            </Button>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ disabled }) => {
    return {
        ...(disabled && {
            opacity: 0.5,
        }),
    };
});

export default SubtaskSection;
